/**
 * Unified Tracking Service for KeffiyehQL Frontend
 *
 * Centralizes all tracking, monitoring, and analytics:
 * - PostHog (User Analytics)
 * - Sentry (Error Tracking) - if configured
 * - Custom Events
 * - Performance Monitoring
 *
 * Provides a single, clean interface for all tracking needs.
 */

import posthog from 'posthog-js'

// Service configuration
interface TrackingConfig {
  posthog?: {
    apiKey?: string
    apiHost?: string
  }
  sentry?: {
    dsn?: string
    environment?: string
  }
  enabled?: boolean
}

// Event properties type
type EventProperties = Record<
  string,
  string | number | boolean | null | undefined | Record<string, unknown> | unknown[]
>

// Performance metric
interface PerformanceMetric {
  name: string
  value: number
  unit?: string
  tags?: Record<string, string>
}

/**
 * Unified Tracking Service
 * Manages all frontend tracking and analytics
 */
class UnifiedTracker {
  private initialized = false
  private config: TrackingConfig = {}
  private servicesStatus = {
    posthog: false,
    sentry: false,
    console: true, // Always available for debugging
  }

  /**
   * Initialize all tracking services
   */
  init(config?: TrackingConfig) {
    if (this.initialized) {
      return
    }

    // Get config from environment or parameters
    this.config = config || {
      posthog: {
        apiKey: import.meta.env.VITE_POSTHOG_KEY,
        apiHost: import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com',
      },
      sentry: {
        dsn: import.meta.env.VITE_SENTRY_DSN,
        environment: import.meta.env.VITE_ENVIRONMENT || 'production',
      },
      enabled: import.meta.env.VITE_TRACKING_ENABLED !== 'false',
    }

    if (!this.config.enabled) {
      console.log('Tracking disabled by configuration')
      return
    }

    // Initialize PostHog
    if (this.config.posthog?.apiKey) {
      try {
        posthog.init(this.config.posthog.apiKey, {
          api_host: this.config.posthog.apiHost,
          autocapture: false,
          capture_pageview: true,
          capture_pageleave: false,
          disable_session_recording: true,
          opt_out_capturing_by_default: false,
          loaded: () => {
            this.servicesStatus.posthog = true
            console.log('PostHog initialized')
          },
        })
      } catch (error) {
        console.error('Failed to initialize PostHog:', error)
      }
    }

    // Initialize Sentry (if needed in future)
    if (this.config.sentry?.dsn) {
      // TODO: Import and initialize Sentry when needed
      // import * as Sentry from '@sentry/react'
      // Sentry.init({ dsn: this.config.sentry.dsn, ... })
      console.log('Sentry configuration detected but not initialized')
    }

    this.initialized = true
  }

  /**
   * Get current status of tracking services
   */
  getStatus() {
    return { ...this.servicesStatus }
  }

  // ==================== EVENTS ====================

  /**
   * Track a generic event
   */
  event(name: string, properties?: EventProperties) {
    if (!this.initialized || !this.config.enabled) {
      return
    }

    try {
      // PostHog
      if (this.servicesStatus.posthog) {
        posthog.capture(name, properties)
      }

      // Console (for debugging)
      if (this.servicesStatus.console && import.meta.env.DEV) {
        console.log(`[Track Event] ${name}`, properties)
      }
    } catch (error) {
      console.error('Failed to track event:', error)
    }
  }

  // ==================== USER TRACKING ====================

  /**
   * Identify a user for tracking
   */
  identifyUser(userId: string, properties?: EventProperties) {
    if (!this.initialized || !this.config.enabled) {
      return
    }

    try {
      // PostHog
      if (this.servicesStatus.posthog) {
        posthog.identify(userId, properties)
      }

      // Sentry (when available)
      // if (this.servicesStatus.sentry) {
      //   Sentry.setUser({ id: userId, ...properties })
      // }

      console.log(`User identified: ${userId}`)
    } catch (error) {
      console.error('Failed to identify user:', error)
    }
  }

  /**
   * Clear user identification (on logout)
   */
  resetUser() {
    if (!this.initialized || !this.config.enabled) {
      return
    }

    try {
      // PostHog
      if (this.servicesStatus.posthog) {
        posthog.reset()
      }

      // Sentry (when available)
      // if (this.servicesStatus.sentry) {
      //   Sentry.setUser(null)
      // }

      console.log('User tracking reset')
    } catch (error) {
      console.error('Failed to reset user:', error)
    }
  }

  // ==================== ERRORS ====================

  /**
   * Track an error
   */
  error(error: Error | string, context?: EventProperties) {
    if (!this.initialized || !this.config.enabled) {
      return
    }

    try {
      const errorData = {
        message: typeof error === 'string' ? error : error.message,
        stack: typeof error === 'object' ? error.stack : undefined,
        ...context,
      }

      // PostHog
      if (this.servicesStatus.posthog) {
        posthog.capture('error_occurred', errorData)
      }

      // Sentry (when available)
      // if (this.servicesStatus.sentry) {
      //   Sentry.captureException(error, { extra: context })
      // }

      // Console
      console.error('[Track Error]', error, context)
    } catch (err) {
      console.error('Failed to track error:', err)
    }
  }

  // ==================== PERFORMANCE ====================

  /**
   * Track a performance metric
   */
  performance(metric: PerformanceMetric) {
    if (!this.initialized || !this.config.enabled) {
      return
    }

    try {
      const perfData = {
        metric_name: metric.name,
        value: metric.value,
        unit: metric.unit || 'ms',
        ...metric.tags,
      }

      // PostHog
      if (this.servicesStatus.posthog) {
        posthog.capture('performance_metric', perfData)
      }

      // Console (in dev mode)
      if (import.meta.env.DEV) {
        console.log(`[Performance] ${metric.name}: ${metric.value}${metric.unit || 'ms'}`)
      }
    } catch (error) {
      console.error('Failed to track performance:', error)
    }
  }

  // ==================== PAGE VIEWS ====================

  /**
   * Track a page view
   */
  pageView(pageName?: string, properties?: EventProperties) {
    if (!this.initialized || !this.config.enabled) {
      return
    }

    try {
      const pageData = {
        $current_url: window.location.href,
        page: pageName || window.location.pathname,
        ...properties,
      }

      // PostHog
      if (this.servicesStatus.posthog) {
        posthog.capture('$pageview', pageData)
      }

      // Console (in dev mode)
      if (import.meta.env.DEV) {
        console.log(`[Page View] ${pageName || window.location.pathname}`)
      }
    } catch (error) {
      console.error('Failed to track page view:', error)
    }
  }

  // ==================== CUSTOM TRACKING ====================

  /**
   * Track a file upload
   */
  upload(letterId: string, fileSize?: number, fileType?: string) {
    this.event('letter_upload', {
      letter_id: letterId,
      file_size: fileSize,
      file_type: fileType,
    })
  }

  /**
   * Track a search
   */
  search(query: string, resultsCount?: number) {
    this.event('search_performed', {
      query: query.substring(0, 100), // Truncate long queries
      results_count: resultsCount,
      page: window.location.pathname,
    })
  }

  /**
   * Track a download
   */
  download(letterId: string, fileName?: string) {
    this.event('letter_downloaded', {
      letter_id: letterId,
      file_name: fileName,
    })
  }

  /**
   * Track language change
   */
  languageChange(from: string, to: string) {
    this.event('language_changed', {
      from_language: from,
      to_language: to,
    })
  }

  // ==================== UTILITIES ====================

  /**
   * Time an operation and track its performance
   */
  async timeOperation<T>(
    name: string,
    operation: () => Promise<T>,
    tags?: Record<string, string>
  ): Promise<T> {
    const startTime = performance.now()

    try {
      const result = await operation()
      const duration = performance.now() - startTime

      this.performance({
        name: `operation_${name}`,
        value: duration,
        unit: 'ms',
        tags: { ...tags, status: 'success' },
      })

      return result
    } catch (error) {
      const duration = performance.now() - startTime

      this.performance({
        name: `operation_${name}`,
        value: duration,
        unit: 'ms',
        tags: { ...tags, status: 'error' },
      })

      this.error(error as Error, { operation: name, duration })
      throw error
    }
  }

  /**
   * Create a timer for manual performance tracking
   */
  startTimer(name: string): () => void {
    const startTime = performance.now()

    return () => {
      const duration = performance.now() - startTime
      this.performance({
        name,
        value: duration,
        unit: 'ms',
      })
    }
  }
}

// ==================== SINGLETON INSTANCE ====================

// Create and export singleton instance
export const tracker = new UnifiedTracker()

// ==================== SIMPLIFIED API ====================

/**
 * Initialize tracking services
 * Should be called once when the app starts
 */
export function initTracking(config?: TrackingConfig) {
  tracker.init(config)
}

/**
 * Track an event
 */
export function triggerEvent(name: string, properties?: EventProperties) {
  tracker.event(name, properties)
}

/**
 * Identify a user
 */
export function identifyUser(userId: string, properties?: EventProperties) {
  tracker.identifyUser(userId, properties)
}

/**
 * Clear user identification
 */
export function resetUser() {
  tracker.resetUser()
}

/**
 * Track an error
 */
export function trackError(error: Error | string, context?: EventProperties) {
  tracker.error(error, context)
}

/**
 * Track performance metric
 */
export function trackPerformance(name: string, value: number, unit?: string) {
  tracker.performance({ name, value, unit })
}

/**
 * Track page view
 */
export function trackPageView(pageName?: string) {
  tracker.pageView(pageName)
}

// ==================== REACT HOOKS ====================

import { useEffect, useCallback } from 'react'

/**
 * Hook to track page views
 */
export function usePageTracking(pageName: string) {
  useEffect(() => {
    tracker.pageView(pageName)
  }, [pageName])
}

/**
 * Hook to track events
 */
export function useEventTracking() {
  return useCallback((name: string, properties?: EventProperties) => {
    tracker.event(name, properties)
  }, [])
}

/**
 * Hook to track errors
 */
export function useErrorTracking() {
  return useCallback((error: Error | string, context?: EventProperties) => {
    tracker.error(error, context)
  }, [])
}

/**
 * Hook for performance tracking
 */
export function usePerformanceTracking() {
  return useCallback((name: string, value: number, unit?: string) => {
    tracker.performance({ name, value, unit })
  }, [])
}

// Export everything
export default tracker
